import moment from "moment";

import { formatCpf, formatMoney, formatName, getCurveColor, perc2color } from '../functions'

function formatRoyalty(royalty) {
  const acquirer = {
    taxId: !!royalty?.acquirer ? royalty.acquirer : '',
    name: !!royalty?.acquirer ? formatName(royalty.acquirer.name) : '',
    nickname: !!royalty?.acquirer ? formatName(royalty.acquirer.nickname) : '',
  }

  const payment = {
    name: !!royalty?.paymentScheme ? formatName(royalty.paymentScheme.name) : '',
    code: !!royalty?.paymentScheme ? royalty.paymentScheme.code : '',
    type: !!royalty?.paymentScheme ? royalty.paymentScheme.type : '',
  }

  return {
    id: royalty._id,
    createdAt: moment(royalty.settlementDate).format('DD/MM/YYYY'),
    amountToPay: royalty?.amountToPay || 0,
    netAmount: royalty?.netAmount || 0,
    acquirer,
    payment
  };
}

function formatPurchaseReport(report) {
  const percent = Math.max(0, ((report.mediaMensal - report.sugestaoCompra) / report.mediaMensal) * 100);

  return {
    description: formatName(report.descricao),
    stock: report.estoque,
    monthlyAverage: formatMoney(report.mediaMensal, 0),
    department: formatName(report.nomeDepartamento),
    brand: formatName(report.nomeMarca),
    quantitySold: Math.round(report.quantidadeVendida * 1000) / 1000,
    suggestionPurchase: report.sugestaoCompra,
    codigoBarras: report.codigoBarras,
    curva: report.abcCode !== "ZZ" ? report.abcCode : '-',
    curveColor: perc2color({ center: 70, percent })
  }
}

function formatPagination(pagination) {
  return {
    page: pagination?.pagina || pagination?.currentPage,
    totalPages: pagination?.totalPaginas || pagination?.totalPages,
    perPage: pagination?.porPagina || pagination?.limit,
    totalDocuments: pagination?.totalDocumentos || pagination.totalDocuments,
    full: pagination,
  };
}


/**
 * A API devolve `averageTicket` ora como numero, ora como string "R$ 12.34".
 * Normaliza para numero antes de formatar.
 */
function toNumber(value) {
  if (typeof value === "number") return value;
  if (!value) return 0;
  const clean = String(value).replace(/[^0-9,.-]/g, "").replace(/\.(?=\d{3}\b)/g, "").replace(",", ".");
  const parsed = Number(clean);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** "SIM" / "NAO" / true / false -> boolean */
function toBool(value) {
  if (typeof value === "boolean") return value;
  return String(value || "").trim().toUpperCase() === "SIM";
}

function toDate(value) {
  if (!value) return null;
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : null;
}

function toAbsenceDays(value) {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function formatCustomerReport(row) {
  return {
    id: row.cpf || row.cellphone || row.name,
    name: formatName(row.name || ""),
    cpf: row.cpf || "",
    cpfLabel: row.cpf ? formatCpf(row.cpf) : "-",
    cellphone: row.cellphone || "-",
    totalAppointments: Number(row.totalAppointments || 0),
    finalizedAppointments: Number(row.finalizedAppointments || 0),
    canceledAppointments: Number(row.canceledAppointments || 0),
    lastAppointment: toDate(row.lastAppointment),
    absenceDays: toAbsenceDays(row.daysSinceLastAppointment),
    averageTicket: toNumber(row.averageTicket),
    totalSales: toNumber(row.totalSales),
    hasPackage: toBool(row.hasPackage),
    isSubscriber: toBool(row.hasSubscription),
    pets: Number(row.pets || 0),
  };
}

function formatCustomerPanel(panel) {
  return {
    customerTotal: Number(panel?.customerTotal || 0),
    withPackage: Number(panel?.withPackage || 0),
    subscribers: Number(panel?.subscribers || 0),
    averageTicket: toNumber(panel?.averageTicket),
    totalSales: toNumber(panel?.totalSales),
  };
}

function formatPetReport(row) {
  return {
    id: `${row.customerCpf || ""}-${row.petName || ""}`,
    petName: formatName(row.petName || ""),
    petBreed: formatName(row.petBreed || ""),
    petSize: formatName(row.petSize || ""),
    customerName: formatName(row.customerName || ""),
    cpf: row.customerCpf || "",
    cpfLabel: row.customerCpf ? formatCpf(row.customerCpf) : "-",
    cellphone: row.cellphone || "-",
    totalAppointments: Number(row.totalAppointments || 0),
    lastAppointment: toDate(row.lastAppointment),
    absenceDays: toAbsenceDays(row.daysSinceLastAppointment),
    averageTicket: toNumber(row.averageTicket),
    totalSales: toNumber(row.totalSales),
    hasPackage: toBool(row.hasPackage),
    isSubscriber: toBool(row.hasSubscription),
  };
}

function formatPetPanel(panel) {
  return {
    petTotal: Number(panel?.petTotal || 0),
    withPackage: Number(panel?.withPackage || 0),
    subscribers: Number(panel?.subscribers || 0),
  };
}

function formatPackageReport(row) {
  return {
    id: `${row.customerCpf || ""}-${row.petName || ""}-${row.lastPackage || ""}`,
    petName: formatName(row.petName || ""),
    customerName: formatName(row.customerName || ""),
    cpf: row.customerCpf || "",
    cpfLabel: row.customerCpf ? formatCpf(row.customerCpf) : "-",
    cellphone: row.customerCellphone || "-",
    petCreatedAt: toDate(row.petCreatedAt),
    registrationDays: Number(row.timeToCreatedAt || 0),
    totalPackages: Number(row.totalPackages || 0),
    servicesOpens: Number(row.servicesOpens || 0),
    bathOpens: Number(row.bathOpens || 0),
    lastPackage: toDate(row.lastPackage),
    revenue: toNumber(row.revenue),
    status: row.status || "-",
  };
}

function formatPackagePanel(panel) {
  return {
    packagesSold: Number(panel?.packagesSold || 0),
    bathOpens: Number(panel?.bathOpens || 0),
    servicesOpens: Number(panel?.servicesOpens || 0),
    revenue: toNumber(panel?.revenue),
  };
}

/**
 * Paginacao do helper `paginator` do backend (reports.js):
 * { page, per_page, pre_page, next_page, total, total_pages }
 * O backend e 1-based; a UI e 0-based. Normaliza aqui, num lugar so.
 */
function formatListPagination(pagination, fallbackPage = 0) {
  const page = Number(pagination?.page);

  return {
    page: Number.isNaN(page) ? fallbackPage : Math.max(0, page - 1),
    totalPages: Number(pagination?.total_pages || 0),
    perPage: Number(pagination?.per_page || 50),
    totalDocuments: Number(pagination?.total || 0),
    full: pagination,
  };
}

export const formatResponse = {
  pagination: (pagination) => formatPagination(pagination),
  listPagination: (pagination, fallbackPage) => formatListPagination(pagination, fallbackPage),
  royalty: (royalty) => formatRoyalty(royalty),
  royalties: (royalties) => {
    if (!royalties.length) return [];
    return royalties.map(formatRoyalty);
  },
  purchaseReport: (report) => formatPurchaseReport(report),
  purchaseReports: (reports) => {
    if (!reports.length) return [];
    return reports.map(formatPurchaseReport);
  },

  customersReport: (rows) => {
    if (!rows?.length) return [];
    return rows.map(formatCustomerReport);
  },
  customersPanel: (panel) => formatCustomerPanel(panel),

  petsReport: (rows) => {
    if (!rows?.length) return [];
    return rows.map(formatPetReport);
  },
  petsPanel: (panel) => formatPetPanel(panel),

  packagesReport: (rows) => {
    if (!rows?.length) return [];
    return rows.map(formatPackageReport);
  },
  packagesPanel: (panel) => formatPackagePanel(panel),
};
